import * as yup from "yup";
import {
    Input,
    message,
    Typography,
    Divider,
    Spin,
    Button,
    Modal,
    Alert
} from "antd";
import {
    Fragment,
    useState,
    useRef,
    useEffect
} from "react";
import GenericPhoneSearch from "./GenericPhoneSearch";
import ZipInfos from "./ZipInfos";
import moment from "moment";
import StatusDisplay from "../StatusDisplay";
import html2canvas from 'html2canvas';
import ClipboardJS from 'clipboard';

import {
    INTERNAL_API_ENDPOINT,
    INTERNAL_CLOSERS_DNC_API_ENDPOINT,
    INTERNAL_VOIP_BLOCKED_API_ENDPOINT,
    INTERNAL_INVALID_PHONES_API_ENDPOINT,
    DATE_FORMAT,
    US_PEOPLE_SEARCH_API
} from "../Constants";
import Litigators from "./Litigators";

yup.addMethod(yup.string, "onlyDigits", function() {
    return this.matches(/^\d+$/, "Only digits allowed.");
});

const { Search } = Input;
const { Title } = Typography;

function SearchPhoneNumber({ uiSettings }) {
    const [searchedPhone, setSearchedPhone] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageCanvas, setImageCanvas] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [personData, setPersonData] = useState(null);
    const [loadingPersonData, setLoadingPersonData] = useState(false);
    const resultsRef = useRef(null);
    const [transferStatus, setTransferStatus] = useState({
        canTransfer: true,
        reasons: []
    });
    const [searchResults, setSearchResults] = useState({
        invalidPhones: null,
        socialAnalytics: null,
        dnc: null,
        blacklisted: null,
        litigators: null
    });

    // Add a state to track if all results are loaded
    const [allResultsLoaded, setAllResultsLoaded] = useState(false);

    const targetDatasets = [
        {
            prefixLabel: "Social Analytics Suggests: ",
            apiEndpoint: INTERNAL_API_ENDPOINT,
            messageCallback: (response) => {
                if (response.data) {
                    return {
                        status: 'found',
                        message: `${response.data.phoneNumber} is HIGH RISK POSSIBLE TCPA is added on ${moment(
            response.data.createdDate
                        ).format(DATE_FORMAT)}`
                    };
                } else {
                    return {
                        status: 'safe',
                        message: `${searchedPhone} is Good`
                    };
                }
            },
        },
        {
            prefixLabel: "Closers DNC Suggests: ",
            apiEndpoint: INTERNAL_CLOSERS_DNC_API_ENDPOINT,
            messageCallback: (response) => {
                if (response.data) {
                    return {
                        status: 'found',
                        message: `${response.data.phoneNumber} is disposed in DNC by several closers added on ${moment(
            response.data.createdDate
                        ).format(DATE_FORMAT)}, PLEASE DO NOT TRANSFER!`
                    };
                } else {
                    return {
                        status: 'safe',
                        message: `${searchedPhone} is Not found, Good to GO!`
                    };
                }
            },
        },
        {
            prefixLabel: "Invalid Phones Suggests: ",
            apiEndpoint: INTERNAL_INVALID_PHONES_API_ENDPOINT,
            messageCallback: (response) => {
                if (response.data) {
                    return {
                        status: 'found',
                        message: `${response.data.phoneNumber} is disposed as INVALID by several closers, PLEASE DO NOT TRANSFER!`
                    };
                } else {
                    return {
                        status: 'safe',
                        message: `${searchedPhone} is Not found, Good to GO!`
                    };
                }
            },
        },
        {
            prefixLabel: "VOIP Blocked Suggests: ",
            apiEndpoint: INTERNAL_VOIP_BLOCKED_API_ENDPOINT,
            messageCallback: (response) => {
                if (response.data) {
                    return {
                        status: 'found',
                        message: `${response.data.phoneNumber} is disposed as Voip Blocked by several closers, PLEASE DO NOT TRANSFER!`
                    };
                } else {
                    return {
                        status: 'safe',
                        message: `${searchedPhone} is Not found, Good to GO!`
                    };
                }
            },
        },
    ];

    async function onSearchPhoneNumber(values) {
        setIsSearching(true);

        // Filter out all non-numeric characters from the input
        const numericValue = values ? values.replace(/\D/g, '') : '';
        
        // Show a message if characters were filtered out
        if (values && values !== numericValue) {
            message.info("Non-numeric characters were automatically removed from your input.");
        }

        if (!numericValue) {
            message.error("Phone number is required.");
            setIsSearching(false);
            return;
        }
        if (numericValue.length !== 10) {
            message.error(`Error: Phone number must be 10 digits.`);
            setIsSearching(false);
            return;
        }

        // Check if the number is already being displayed
        if (searchedPhone === numericValue) {
            message.info("This number is already displayed.");
            setIsSearching(false);
            return;
        }

        // Reset all result states
        setAllResultsLoaded(false);
        setSearchResults({
            invalidPhones: null,
            socialAnalytics: null,
            dnc: null,
            blacklisted: null,
            litigators: null
        });
        setTransferStatus({
            canTransfer: true,
            reasons: []
        });

        // Small delay to show loading
        setTimeout(() => {
            setSearchedPhone(numericValue);
            setIsSearching(false);
        }, 300);
    }

    const handleClearSearch = () => {
        setSearchedPhone("");
    };

    const captureScreenshot = () => {
        if (!resultsRef.current) return;
        
        try {
            setIsCapturing(true);
            
            // Apply a temporary fixed width to eliminate side spacing
            const originalWidth = resultsRef.current.style.width;
            resultsRef.current.style.width = "800px";
            resultsRef.current.style.margin = "0 auto";
            
            html2canvas(resultsRef.current, {
                logging: false,
                useCORS: true,
                scale: 2,
                backgroundColor: '#ffffff',
                width: 800,  // Fixed width to match content
                x: 0,        // Start at the exact left edge of the element
                windowWidth: 800, // Virtual viewport width
                removeContainer: false
            }).then(canvas => {
                // Restore original styles
                resultsRef.current.style.width = originalWidth;
                
                // Directly copy to clipboard
                canvas.toBlob(async (blob) => {
                    try {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': blob
                            })
                        ]);
                        message.success('Screenshot copied to clipboard');
                    } catch (err) {
                        console.error('Failed to copy image: ', err);
                        message.error('This browser does not support copying images to clipboard');
                    }
                    setIsCapturing(false);
                });
            }).catch(err => {
                // Restore original styles on error too
                resultsRef.current.style.width = originalWidth;
                
                console.error('Error in html2canvas', err);
                message.error('Failed to create screenshot');
                setIsCapturing(false);
            });
            
        } catch (error) {
            console.error('Error taking screenshot:', error);
            message.error('Failed to take screenshot');
            setIsCapturing(false);
        }
    };

    const copyImageToClipboard = async () => {
        try {
            if (imageCanvas) {
                imageCanvas.toBlob(async (blob) => {
                    try {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': blob
                            })
                        ]);
                        message.success('Image copied to clipboard - paste it in your application now');
                        setShowImageModal(false);
                    } catch (err) {
                        console.error('Failed to copy image: ', err);
                        message.error('This browser does not support copying images to clipboard');
                    }
                });
            }
        } catch (err) {
            console.error('Error copying image: ', err);
            message.error('Failed to copy image to clipboard');
        }
    };

    const downloadImage = () => {
        if (!imagePreview) return;
        
        const link = document.createElement('a');
        link.href = imagePreview;
        link.download = `phone-search-${searchedPhone}.png`;
        link.click();
        message.success('Screenshot downloaded successfully');
    };

    useEffect(() => {
        // Reset results if phone number is cleared
        if (!searchedPhone) {
            setPersonData(null);
            setAllResultsLoaded(false);
            setSearchResults({
                invalidPhones: null,
                socialAnalytics: null,
                dnc: null,
                blacklisted: null,
                litigators: null
            });
            return;
        }
        
        // Fetch person data for the phone number
        fetchPersonData(searchedPhone);
    }, [searchedPhone]);

    const fetchPersonData = async (phoneNumber) => {
        setLoadingPersonData(true);
        try {
            const axios = require("axios").default;
            const response = await axios.get(`${US_PEOPLE_SEARCH_API}${phoneNumber}`);
            
            if (response.data && response.data.status === "ok" && response.data.count > 0) {
                setPersonData(response.data);
            } else {
                setPersonData(null);
            }
        } catch (error) {
            console.error("Error fetching person data:", error);
            setPersonData(null);
        } finally {
            setLoadingPersonData(false);
        }
    };

    const updateTransferStatus = (results) => {
        // Default state - can transfer
        const status = {
            canTransfer: true,
            reasons: []
        };

        // If any result is null or undefined, we're still loading
        if (!results.invalidPhones || 
            !results.socialAnalytics || 
            !results.dnc || 
            !results.blacklisted || 
            !results.litigators) {
            setAllResultsLoaded(false);
            return;
        }

        // Check if any results have empty status (indicating loading)
        if (results.invalidPhones.status === "" || 
            results.socialAnalytics.status === "" || 
            results.dnc.status === "" || 
            results.blacklisted.status === "" || 
            results.litigators.status === "") {
            setAllResultsLoaded(false);
            return;
        }
    
        // If we got here, all results are loaded
        setAllResultsLoaded(true);

        // Check for "found" statuses in results that indicate non-transferability
        if (results.invalidPhones.status === 'found') {
            status.canTransfer = false;
            status.reasons.push("Invalid Phones");
        }
        if (results.socialAnalytics.status === 'found') {
            status.canTransfer = false;
            status.reasons.push("Social Analytics");
        }
        if (results.dnc.status === 'found') {
            status.canTransfer = false;
            status.reasons.push("DNC");
        }
        if (results.blacklisted.status === 'found') {
            status.canTransfer = false;
            status.reasons.push("BlackListed DB");
        }
        if (results.litigators.status === 'found') {
            status.canTransfer = false;
            status.reasons.push("Litigators DB");
        }
    
        setTransferStatus(status);
    };

    // Update a specific result in the searchResults object
    const updateSearchResult = (key, result) => {
        setSearchResults(prev => {
            const newResults = { ...prev, [key]: result };
            updateTransferStatus(newResults);
            return newResults;
        });
    };

    const renderSearchResults = () => {
        if (!searchedPhone) return null;

        const personInfo = personData?.person?.[0];
        const mainAddress = personInfo?.addresses?.[0];

        return (
            <div className="search-results" style={{ 
                width: '100%', 
                margin: '0 auto', 
                textAlign: 'center',
                paddingBottom: '80px'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {allResultsLoaded ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            backgroundColor: transferStatus.canTransfer ? '#ebfaeb' : '#ffebeb',
                            border: transferStatus.canTransfer ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
                            color: transferStatus.canTransfer ? '#28a745' : '#dc3545',
                            fontWeight: 'bold'
                        }}>
                            {transferStatus.canTransfer 
                                ? "You can transfer" 
                                : <div>
                                    <div>DO NOT TRANSFER</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                        {transferStatus.reasons.length > 0 && "The following reasons were found:"}
                                        <ul style={{ margin: '4px 0 0 0', padding: '0 0 0 20px', textAlign: 'left' }}>
                                            {transferStatus.reasons.map((reason, index) => (
                                                <li key={index}>{reason}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            }
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            backgroundColor: '#f9f9f9',
                            border: '1px solid #f0f0f0'
                        }}>
                            <div className="loader loader-small" style={{ marginRight: '8px' }}></div>
                            Checking all data sources...
                        </div>
                    )}
                    <Button 
                        onClick={captureScreenshot} 
                        type="primary" 
                        style={{ background: '#1890ff', borderColor: '#1890ff' }}
                        loading={isCapturing}
                    >
                        {isCapturing ? 'Capturing...' : 'Take Screenshot'}
                    </Button>
                </div>
                
                <div ref={resultsRef} style={{ width: '800px', margin: '0 auto' }}>
                    <div style={{ 
                        border: '1px dashed #ccc', 
                        fontWeight: '700',
                        padding: '15px', 
                        marginBottom: '3px',
                        borderRadius: '4px',
                        background: '#f9f9f9',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        {loadingPersonData ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px' }}>
                                <div className="loader loader-small" style={{ marginRight: '8px' }}></div> Loading person data...
                            </div>
                        ) : (
                            <div>
                                <span style={{ color: '#e63946', fontWeight: 'bold', margin: '0 5px' }}>Name:</span> 
                                {personInfo?.name || 'N/A'}
                                
                                <span style={{ color: '#e63946', fontWeight: 'bold', margin: '0 5px' }}> Age:</span> 
                                {personInfo?.age || 'N/A'}
                                
                                <span style={{ color: '#e63946', fontWeight: 'bold', margin: '0 5px' }}> Full Address:</span> 
                                {mainAddress ? 
                                    `${mainAddress.home}, ${mainAddress.city}, ${mainAddress.state} ${mainAddress.zip}` 
                                    : 'N/A'}
                            </div>
                        )}
                    </div>
                    
                    <div className="flat-results" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        <ZipInfos phoneNumber={searchedPhone} />
                        <Litigators 
                            phoneNumber={searchedPhone} 
                            onResultUpdate={(result) => updateSearchResult('litigators', result)} 
                        />
                        <Litigators 
                            prefixLabel="BlackListed DB Suggests: " 
                            phoneNumber={searchedPhone} 
                            fakeBlackList={true} 
                            onResultUpdate={(result) => updateSearchResult('blacklisted', result)} 
                        />
                        <Litigators 
                            prefixLabel="DNC Suggests: " 
                            phoneNumber={searchedPhone} 
                            fakeDncScrub={true} 
                            onResultUpdate={(result) => updateSearchResult('dnc', result)} 
                        />
                        {targetDatasets.map((targetDataset, index) => (
                            <GenericPhoneSearch 
                                key={index}
                                prefixLabel={targetDataset.prefixLabel}
                                phoneNumber={searchedPhone}
                                apiEndpoint={targetDataset.apiEndpoint}
                                messageCallback={targetDataset.messageCallback}
                                onResultUpdate={(result) => {
                                    if (targetDataset.prefixLabel === "Invalid Phones Suggests: ") {
                                        updateSearchResult('invalidPhones', result);
                                    } else if (targetDataset.prefixLabel === "Social Analytics Suggests: ") {
                                        updateSearchResult('socialAnalytics', result);
                                    }
                                }}
                            />
                        ))}
                    </div>
                </div>

                <Modal
                    title="Screenshot Preview"
                    open={showImageModal}
                    onCancel={() => setShowImageModal(false)}
                    footer={[
                        <Button key="download" onClick={downloadImage} type="default">
                            Download
                        </Button>,
                        <Button key="copy" onClick={copyImageToClipboard} type="primary">
                            Copy to Clipboard
                        </Button>,
                    ]}
                    width={850}
                >
                    <div style={{ textAlign: 'center' }}>
                        <Alert
                            message="Browser Compatibility Note"
                            description="Copying images to clipboard requires a modern browser. If it doesn't work in your application, please use the Download option instead."
                            type="info"
                            showIcon
                            style={{ marginBottom: '15px' }}
                        />
                        {imagePreview && (
                            <img 
                                src={imagePreview} 
                                alt="Screenshot preview" 
                                style={{ maxWidth: '100%', border: '1px solid #eee' }} 
                            />
                        )}
                    </div>
                </Modal>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="search-container" style={{ 
                textAlign: 'center', 
                marginBottom: '20px',
                padding: '20px 0',
                width: '100%'
            }}>
            
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <Search
                        placeholder="Enter 10-digit Phone Number"
                        style={{
                            width: "300px",
                            borderRadius: '4px'
                        }}
                      allowClear
                        enterButton={
                            <Button 
                                type="primary" 
                                style={{ 
                                    background: '#e63946', 
                                    borderColor: '#e63946'
                                }}
                            >
                                Search
                            </Button>
                        }
                      size="large"
                        onSearch={onSearchPhoneNumber}
                        disabled={isSearching}
                    />
                </div>
            </div>
            
            {renderSearchResults()}
        </Fragment>
    );
}

export default SearchPhoneNumber;